import React, { useState } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment,
  IconButton,
  Divider,
  Collapse
} from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import { useLanguageNavigation } from '../hooks/useLanguageNavigation';
import { useTranslation } from 'react-i18next';
import { Capacitor } from '@capacitor/core';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import GoogleIcon from '@mui/icons-material/Google';
import AppleIcon from '@mui/icons-material/Apple';
import EmailIcon from '@mui/icons-material/Email';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';

function EnhancedLoginForm() {
  const { signInWithGoogle, signInWithApple, signUpWithEmail, signInWithEmail, resetPassword } = useAuth();
  const { navigateWithLang } = useLanguageNavigation();
  const { t } = useTranslation();
  
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [isSignUp, setIsSignUp] = useState(false);
  const [isSigningIn, setIsSigningIn] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showResetDialog, setShowResetDialog] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Form states
  const [signInForm, setSignInForm] = useState({ email: '', password: '' });
  const [signUpForm, setSignUpForm] = useState({ email: '', password: '', confirmPassword: '', displayName: '' });
  const [resetForm, setResetForm] = useState({ email: '' });

  const handleEmailFormToggle = () => {
    setShowEmailForm(!showEmailForm);
    setError('');
    setSuccess('');
  };

  const handleSignUpToggle = () => {
    setIsSignUp(!isSignUp);
    setError('');
    setSuccess('');
  };

  const handleGoogleSignIn = async () => {
    try {
      setIsSigningIn(true);
      setError('');
      await signInWithGoogle();
      navigateWithLang('portfolio');
    } catch (error) {
      console.error('Google Sign-In Error:', error);
      setError('Failed to sign in with Google. Please try again.');
    } finally {
      setIsSigningIn(false);
    }
  };

  const handleAppleSignIn = async () => {
    try {
      setIsSigningIn(true);
      setError('');
      await signInWithApple();
      navigateWithLang('portfolio');
    } catch (error) {
      console.error('Apple Sign-In Error:', error);
      setError('Failed to sign in with Apple. Please try again.');
    } finally {
      setIsSigningIn(false);
    }
  };

  const handleEmailSignIn = async (e) => {
    e.preventDefault();
    if (!signInForm.email || !signInForm.password) {
      setError('Please fill in all fields');
      return;
    }

    try {
      setIsSigningIn(true);
      setError('');
      await signInWithEmail(signInForm.email, signInForm.password);
      navigateWithLang('portfolio');
    } catch (error) {
      console.error('Email Sign-In Error:', error);
      if (error.code === 'auth/user-not-found') {
        setError('No account found with this email address.');
      } else if (error.code === 'auth/wrong-password') {
        setError('Incorrect password.');
      } else if (error.code === 'auth/invalid-email') {
        setError('Invalid email address.');
      } else {
        setError('Failed to sign in. Please check your credentials.');
      }
    } finally {
      setIsSigningIn(false);
    }
  };

  const handleEmailSignUp = async (e) => {
    e.preventDefault();
    if (!signUpForm.email || !signUpForm.password || !signUpForm.confirmPassword) {
      setError('Please fill in all fields');
      return;
    }

    if (signUpForm.password !== signUpForm.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (signUpForm.password.length < 6) {
      setError('Password must be at least 6 characters long');
      return;
    }

    try {
      setIsSigningIn(true);
      setError('');
      await signUpWithEmail(signUpForm.email, signUpForm.password, signUpForm.displayName);
      setSuccess('Account created successfully! You are now signed in.');
      navigateWithLang('portfolio');
    } catch (error) {
      console.error('Email Sign-Up Error:', error);
      if (error.code === 'auth/email-already-in-use') {
        setError('An account with this email already exists.');
      } else if (error.code === 'auth/invalid-email') {
        setError('Invalid email address.');
      } else if (error.code === 'auth/weak-password') {
        setError('Password is too weak. Please choose a stronger password.');
      } else {
        setError('Failed to create account. Please try again.');
      }
    } finally {
      setIsSigningIn(false);
    }
  };

  const handlePasswordReset = async () => {
    if (!resetForm.email) {
      setError('Please enter your email address');
      return;
    }

    try {
      await resetPassword(resetForm.email);
      setSuccess('Password reset email sent! Check your inbox.');
      setShowResetDialog(false);
      setResetForm({ email: '' });
    } catch (error) {
      console.error('Password Reset Error:', error);
      if (error.code === 'auth/user-not-found') {
        setError('No account found with this email address.');
      } else {
        setError('Failed to send reset email. Please try again.');
      }
    }
  };

  return (
    <Box sx={{ width: '100%', maxWidth: 400, mx: 'auto' }}>
      <Typography component="h1" variant="h4" sx={{ fontWeight: 'bold', mb: 3, textAlign: 'center' }}>
        {t('login.welcome')}
      </Typography>

      {/* Benefits list */}
      <Box sx={{ mb: 4, textAlign: 'left' }}>
        <Box component="ul" sx={{ listStyle: 'none', padding: 0, margin: 0 }}>
          <Box component="li" sx={{ mb: 0.5, display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: 'primary.main', flexShrink: 0 }} />
            <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
              {t('login.benefit1')}
            </Typography>
          </Box>
          <Box component="li" sx={{ mb: 0.5, display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: 'primary.main', flexShrink: 0 }} />
            <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
              {t('login.benefit2')}
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Error/Success Messages */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      {/* Main Login Buttons */}
      <Box sx={{ display: 'flex', gap: 1.5, mb: 3, flexDirection: { xs: 'column', sm: 'row' } }}>
        {/* Google Sign-In Button */}
        <Button
          variant="contained"
          onClick={handleGoogleSignIn}
          disabled={isSigningIn}
          fullWidth
          size="medium"
          startIcon={<GoogleIcon />}
          sx={{ 
            py: 1,
            backgroundColor: '#4285f4',
            '&:hover': { backgroundColor: '#3367d6' },
            fontSize: '0.9rem',
            '& .MuiButton-startIcon': {
              marginRight: 1
            }
          }}
        >
          Google
        </Button>

        {/* Apple Sign-In Button - Hide on Android */}
        {Capacitor.getPlatform() !== 'android' && (
          <Button
            variant="contained"
            onClick={handleAppleSignIn}
            disabled={isSigningIn}
            fullWidth
            size="medium"
            startIcon={<AppleIcon />}
            sx={{ 
              py: 1,
              backgroundColor: '#000000',
              '&:hover': { backgroundColor: '#333333' },
              fontSize: '0.9rem',
              '& .MuiButton-startIcon': {
                marginRight: 1.5
              }
            }}
          >
            Apple
          </Button>
        )}

        {/* Email/Password Button */}
        <Button
          variant="outlined"
          onClick={handleEmailFormToggle}
          disabled={isSigningIn}
          fullWidth
          size="medium"
          startIcon={<EmailIcon />}
          endIcon={showEmailForm ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          sx={{
            py: 1,
            fontSize: '0.9rem',
            '& .MuiButton-startIcon': {
              marginRight: 1
            }
          }}
        >
          Email
        </Button>
      </Box>

      {/* Collapsible Email/Password Form */}
      <Collapse in={showEmailForm}>
        <Box sx={{ mt: 2 }}>
          <Divider sx={{ mb: 3 }}>
            <Typography variant="body2" color="text.secondary">
              {isSignUp ? 'Create Account' : 'Sign In'}
            </Typography>
          </Divider>

          {/* Toggle between Sign In and Sign Up */}
          <Box sx={{ textAlign: 'center', mb: 2 }}>
            <Button
              variant="text"
              size="small"
              onClick={handleSignUpToggle}
            >
              {isSignUp ? 'Already have an account? Sign In' : "Don't have an account? Sign Up"}
            </Button>
          </Box>

          {/* Sign In Form */}
          {!isSignUp && (
            <Box component="form" onSubmit={handleEmailSignIn}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={signInForm.email}
                onChange={(e) => setSignInForm({ ...signInForm, email: e.target.value })}
                margin="normal"
                required
                autoComplete="email"
              />
              <TextField
                fullWidth
                label="Password"
                type={showPassword ? 'text' : 'password'}
                value={signInForm.password}
                onChange={(e) => setSignInForm({ ...signInForm, password: e.target.value })}
                margin="normal"
                required
                autoComplete="current-password"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <Box sx={{ textAlign: 'right', mb: 2 }}>
                <Button
                  variant="text"
                  size="small"
                  onClick={() => setShowResetDialog(true)}
                >
                  Forgot Password?
                </Button>
              </Box>
              <Button
                type="submit"
                variant="contained"
                fullWidth
                size="large"
                disabled={isSigningIn}
              >
                {isSigningIn ? 'Signing In...' : 'Sign In'}
              </Button>
            </Box>
          )}

          {/* Sign Up Form */}
          {isSignUp && (
            <Box component="form" onSubmit={handleEmailSignUp}>
              <TextField
                fullWidth
                label="Full Name"
                value={signUpForm.displayName}
                onChange={(e) => setSignUpForm({ ...signUpForm, displayName: e.target.value })}
                margin="normal"
                autoComplete="name"
              />
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={signUpForm.email}
                onChange={(e) => setSignUpForm({ ...signUpForm, email: e.target.value })}
                margin="normal"
                required
                autoComplete="email"
              />
              <TextField
                fullWidth
                label="Password"
                type={showPassword ? 'text' : 'password'}
                value={signUpForm.password}
                onChange={(e) => setSignUpForm({ ...signUpForm, password: e.target.value })}
                margin="normal"
                required
                autoComplete="new-password"
                helperText="Password must be at least 6 characters long"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <TextField
                fullWidth
                label="Confirm Password"
                type={showPassword ? 'text' : 'password'}
                value={signUpForm.confirmPassword}
                onChange={(e) => setSignUpForm({ ...signUpForm, confirmPassword: e.target.value })}
                margin="normal"
                required
                autoComplete="new-password"
              />
              <Button
                type="submit"
                variant="contained"
                fullWidth
                size="large"
                disabled={isSigningIn}
                sx={{ mt: 2 }}
              >
                {isSigningIn ? 'Creating Account...' : 'Create Account'}
              </Button>
            </Box>
          )}
        </Box>
      </Collapse>

      {/* Password Reset Dialog */}
      <Dialog open={showResetDialog} onClose={() => setShowResetDialog(false)}>
        <DialogTitle>Reset Password</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Enter your email address and we'll send you a link to reset your password.
          </Typography>
          <TextField
            fullWidth
            label="Email"
            type="email"
            value={resetForm.email}
            onChange={(e) => setResetForm({ email: e.target.value })}
            margin="normal"
            required
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowResetDialog(false)}>Cancel</Button>
          <Button onClick={handlePasswordReset} variant="contained">
            Send Reset Email
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default EnhancedLoginForm;
